import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "../../../utils/api/axios";
import { HiCalendar, HiClock, HiCheckCircle, HiExclamationCircle, HiRefresh, HiPencil, HiTrash, HiPlus, HiEye, HiShieldCheck, HiLightningBolt, HiSearch, HiPause, HiPlay, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { FiLayers, FiGlobe, FiSmartphone, FiServer, FiCloud, FiTarget, FiMinus, FiCheck, FiStar } from "react-icons/fi";
import { FaShieldAlt } from "react-icons/fa";
import useProgram from "../../../utils/hooks/programs/useProgram";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import toast from "react-hot-toast";
import PentestReportConfirmationModal from "../../../components/common/PentestReportConfirmationModal";
import { createAutomatedReport } from "../../../utils/api/endpoints/automated-reports/automated-reports";
import { useGetProgramsNeedingFeedbackQuery } from "../../../utils/api/endpoints/program-feedback/programFeedbackApi";
import { useRequestFeedbackMutation, useUndoRequestFeedbackMutation } from "../../../utils/api/endpoints/admin/adminFeedbackApi";
import { FiMessageSquare, FiX, FiRotateCcw, FiDownload } from "react-icons/fi";

// Helper for class names
const clsx = (...classes: (string | boolean | undefined | null | false)[]) => classes.filter(Boolean).join(' ');

interface ProgramRun {
    run_id: number;
    run_title: string;
    start_date: string;
    end_date: string;
    status: "scheduled" | "active" | "completed" | "archived" | "paused";
    run_type: "quick" | "comprehensive";
    is_scope_verified: boolean;
    is_scope_submitted: boolean;
    settings_snapshot?: string;
    paused_at?: string;
    createdAt: string;
    release_notes?: string;
    business_priorities?: string;
    feedback?: {
        feedback_id: number;
        status: string;
        overall_program_rating?: number;
        is_skipped?: boolean;
        createdAt: string;
    };
}

interface QuotaInfo {
    quota: number;
    used: number;
    remaining: number;
    duration: number;
}

interface CompanyQuota {
    quick: QuotaInfo;
    comprehensive: QuotaInfo;
}

interface ScheduledRunsProps {
    triggerAutoOpen?: number;
}

const ScheduledRuns: React.FC<ScheduledRunsProps> = ({ triggerAutoOpen = 0 }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { program, refetch: refetchProgram } = useProgram(id ? parseInt(id, 10) : undefined);
    const { role } = useUserCredentials();
    const [runs, setRuns] = useState<ProgramRun[]>([]);
    const [reports, setReports] = useState<any[]>([]);
    const [quota, setQuota] = useState<CompanyQuota | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasScrolled, setHasScrolled] = useState(false);


    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
    const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
    const [editingRun, setEditingRun] = useState<ProgramRun | null>(null);
    const [selectedRunSnapshot, setSelectedRunSnapshot] = useState<any>(null);
    const [verifying, setVerifying] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [rescheduleDate, setRescheduleDate] = useState("");
    const [rescheduleEndDate, setRescheduleEndDate] = useState("");
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isDirectVerifyConfirmOpen, setIsDirectVerifyConfirmOpen] = useState(false);
    const [selectedRunForReport, setSelectedRunForReport] = useState<number | undefined>(undefined);
    const [isRequestingReport, setIsRequestingReport] = useState(false);
    const [isEditingSnapshot, setIsEditingSnapshot] = useState(false);
    const [tempSnapshot, setTempSnapshot] = useState<any>(null);
    const [savingSnapshot, setSavingSnapshot] = useState(false);
    const [availableSnapshotTargets, setAvailableSnapshotTargets] = useState<any[]>([]);
    const [loadingAvailable, setLoadingAvailable] = useState(false);
    const [savingRun, setSavingRun] = useState(false);
    const targetDetailsCache = useRef<Map<number, any>>(new Map());

    // Snapshot Modal Search & Filters
    const [snapshotSearchTerm, setSnapshotSearchTerm] = useState("");
    const [snapshotActiveFilter, setSnapshotActiveFilter] = useState<'all' | 'web' | 'api' | 'mobile' | 'infra' | 'cloud' | 'custom'>('all');

    const filteredSnapshotTargets = useMemo(() => {
        const targs = tempSnapshot?.targets || selectedRunSnapshot?.snapshot?.targets || [];
        return targs.filter((t: any) => {
            const lowSearch = snapshotSearchTerm.toLowerCase().trim();
            const matchesSearch = !lowSearch || 
                                 t.target_name.toLowerCase().includes(lowSearch) || 
                                 t.target_value.toLowerCase().includes(lowSearch);
            
            const lowType = (t.target_type || '').toLowerCase();
            const matchesFilter = snapshotActiveFilter === 'all' || 
                                 (snapshotActiveFilter === 'web' && (lowType.includes('web') || lowType.includes('url') || lowType.includes('callback'))) ||
                                 (snapshotActiveFilter === 'api' && lowType.includes('api')) ||
                                 (snapshotActiveFilter === 'mobile' && (lowType.includes('mobile') || lowType.includes('android') || lowType.includes('ios'))) ||
                                 (snapshotActiveFilter === 'infra' && (lowType.includes('external ip') || lowType.includes('cidr') || lowType.includes('infrastructure') || lowType.includes('host') || lowType.includes('network'))) ||
                                 (snapshotActiveFilter === 'cloud' && (lowType.includes('cloud') || lowType.includes('project') || lowType.includes('account'))) ||
                                 (snapshotActiveFilter === 'custom' && lowType.includes('custom'));
            
            return matchesSearch && matchesFilter;
        });
    }, [tempSnapshot, selectedRunSnapshot, snapshotSearchTerm, snapshotActiveFilter]);

    const filteredAvailableTargets = useMemo(() => {
        return availableSnapshotTargets.filter((t: any) => {
            const lowSearch = snapshotSearchTerm.toLowerCase().trim();
            const matchesSearch = !lowSearch || 
                                 t.target_name.toLowerCase().includes(lowSearch) || 
                                 t.target_value.toLowerCase().includes(lowSearch);
            
            const lowType = (t.target_type || '').toLowerCase();
            const matchesFilter = snapshotActiveFilter === 'all' || 
                                 (snapshotActiveFilter === 'web' && (lowType.includes('web') || lowType.includes('url') || lowType.includes('callback'))) ||
                                 (snapshotActiveFilter === 'api' && lowType.includes('api')) ||
                                 (snapshotActiveFilter === 'mobile' && (lowType.includes('mobile') || lowType.includes('android') || lowType.includes('ios'))) ||
                                 (snapshotActiveFilter === 'infra' && (lowType.includes('external ip') || lowType.includes('cidr') || lowType.includes('infrastructure') || lowType.includes('host') || lowType.includes('network'))) ||
                                 (snapshotActiveFilter === 'cloud' && (lowType.includes('cloud') || lowType.includes('project') || lowType.includes('account'))) ||
                                 (snapshotActiveFilter === 'custom' && lowType.includes('custom'));
            
            return matchesSearch && matchesFilter;
        });
    }, [availableSnapshotTargets, snapshotSearchTerm, snapshotActiveFilter]);

    // Form states
    const [formData, setFormData] = useState({
        run_title: "",
        start_date: "",
        run_type: "quick" as "quick" | "comprehensive",
        status: "scheduled" as "scheduled" | "active" | "completed" | "archived" | "paused",
        release_notes: "",
        business_priorities: ""
    });

    const isBusinessUser = [UserRole.BUSINESS, UserRole.BUSINESS_ADMINISTRATOR, UserRole.BUSINESS_MANAGER].includes(role as UserRole);
    const isFeedbackAllowedBusinessUser = [UserRole.BUSINESS, UserRole.BUSINESS_ADMINISTRATOR].includes(role as UserRole);
    const isAdmin = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(role as UserRole);
    const isSuperAdmin = [UserRole.ADMIN, UserRole.SUB_ADMIN].includes(role as UserRole);
    const canManageRuns = isAdmin || isBusinessUser;
    const isAuthorizedForReport = role && [
        UserRole.BUSINESS,
        UserRole.ADMIN,
        UserRole.SUB_ADMIN,
        UserRole.QA
    ].includes(role as UserRole);

    const { data: allPendingFeedbacks } = useGetProgramsNeedingFeedbackQuery(undefined, {
        skip: !id || !isFeedbackAllowedBusinessUser
    });
    const pendingFeedbacksForThisProgram = Array.isArray(allPendingFeedbacks) 
        ? allPendingFeedbacks.filter(f => f.program_id === parseInt(id!, 10)) 
        : [];

    const hasAnyFeedback = useMemo(() => {
        return runs.some(run => Boolean(run.feedback)) || pendingFeedbacksForThisProgram.length > 0;
    }, [runs, pendingFeedbacksForThisProgram]);


    const fetchRuns = async () => {
        if (!id) return;
        try {
            const response = await axios.get(`/v2/programs/${id}/runs`);
            setRuns(response.data.data || []);
        } catch (error) {
            console.error("Error fetching runs:", error);
            setRuns([]);
        }
    };

    const fetchReports = async () => {
        try {
            let res;
            if (role && [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.QA].includes(role as UserRole)) {
                const response = await axios.get(`/v2/automated-report/admin/pentest-reports?limit=100&program_id=${id}`);
                res = response.data;
            } else {
                try {
                    // Try getting business-reports (under-review or pending automated reports versions)
                    const response = await axios.get('/v2/automated-report/business-reports');
                    res = response.data;
                } catch (err) {
                    console.warn("Could not fetch business reports directly:", err);
                    res = { status: 'success', data: { pending_reports: [] } };
                }
            }
            if (res && res.status === 'success') {
                const rawData = res.data || {};
                const reportsList = [
                    ...(rawData.reports || []),
                    ...(rawData.pending_reports || []),
                    ...(rawData.approved_reports || []),
                    ...(res.pending_reports || []),
                    ...(res.approved_reports || []),
                    ...(res.reports || [])
                ];
                console.log("ScheduledRuns fetchReports raw response:", res, "reportsList parsed:", reportsList);
                setReports(reportsList);
            }
        } catch (error) {
            console.error("Error fetching reports in ScheduledRuns:", error);
        }
    };

    const runReportStateMap = useMemo(() => {
        const map = new Map<number, any>();
        if (!reports || reports.length === 0) return map;

        const grouped = new Map<number, any[]>();
        for (const item of reports) {
            const reportObj = item.report || item;
            const rId = reportObj.run_id !== undefined ? reportObj.run_id : reportObj.runId;
            if (rId !== undefined && rId !== null) {
                const numId = Number(rId);
                const list = grouped.get(numId) || [];
                list.push(item);
                grouped.set(numId, list);
            }
        }

        for (const [runId, runReports] of grouped.entries()) {
            const approvedReports = runReports.filter(item => {
                const reportObj = item.report || item;
                const reportStatus = item.report ? reportObj.status : (item.status || reportObj.status);
                return ['approved', 'business_approved', 'business_review', 'business_requested_changes'].includes(reportStatus);
            });

            const pendingReport = runReports.find(item => {
                const reportObj = item.report || item;
                const reportStatus = item.report ? reportObj.status : (item.status || reportObj.status);
                return ['pending', 'draft', 'qa_review', 'admin_review', 'rejected', 'changes_added', 'report_updated'].includes(reportStatus);
            });

            let latestReportObj = null;
            if (runReports.length > 0) {
                const sortedReports = [...runReports].sort((a, b) => {
                    const aObj = a.report || a;
                    const bObj = b.report || b;
                    return bObj.id - aObj.id;
                });
                latestReportObj = sortedReports[0].report || sortedReports[0];
            }

            if (pendingReport) {
                const reportObj = pendingReport.report || pendingReport;
                map.set(runId, { status: 'requested', reportId: reportObj.id, approvedReports, latestReportObj });
            } else if (approvedReports.length > 0) {
                const reportObj = approvedReports[0].report || approvedReports[0];
                map.set(runId, { status: 'generated', reportId: reportObj.id, approvedReports, latestReportObj });
            } else {
                map.set(runId, { status: 'none', reportId: null, approvedReports: [], latestReportObj });
            }
        }

        return map;
    }, [reports]);

    const defaultReportState = useMemo(() => ({ status: 'none', reportId: null, approvedReports: [], latestReportObj: null }), []);

    const getRunReportState = useCallback((runId: number) => {
        return runReportStateMap.get(runId) || defaultReportState;
    }, [runReportStateMap, defaultReportState]);

    const [requestFeedbackMutation, { isLoading: isRequestingFeedback }] = useRequestFeedbackMutation();
    const [undoRequestFeedbackMutation, { isLoading: isUndoingFeedback }] = useUndoRequestFeedbackMutation();

    const executeRunRequestFeedback = async (runId: number) => {
        if (!program || !program.userId || !id) return;
        try {
            await requestFeedbackMutation({
                program_id: parseInt(id, 10),
                run_id: runId,
                business_user_id: program.userId
            }).unwrap();
            toast.success("Feedback requested successfully for this run!");
            fetchRuns();
        } catch (error: any) {
            toast.error(error?.data?.message || "Failed to request feedback");
        }
    };

    const executeRunUndoFeedback = async (runId: number) => {
        if (!program || !program.userId || !id) return;
        try {
            await undoRequestFeedbackMutation({
                program_id: parseInt(id, 10),
                run_id: runId,
                business_user_id: program.userId
            }).unwrap();
            toast.success("Feedback request taken back successfully for this run!");
            fetchRuns();
        } catch (error: any) {
            toast.error(error?.data?.message || "Failed to take back feedback request");
        }
    };

    const fetchQuota = async () => {
        if (!isBusinessUser && !isAdmin) return;
        try {
            let url = '/v2/program-runs/quota';
            if (isAdmin && program) {
                const companyId = program.companyId;
                if (!companyId) return; // Prevent 400 if ID not yet available
                url += `?company_id=${companyId}`;
            }
            const response = await axios.get(url);
            setQuota(response.data);
        } catch (error) {
            console.error("Error fetching quota:", error);
        }
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([fetchRuns(), fetchReports()]);
            setLoading(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, role]);

    useEffect(() => {
        fetchQuota();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, program, role]);

    useEffect(() => {
        if (triggerAutoOpen > 0 && !loading && runs.length > 0) {
            const pendingRun = runs.find(r => !r.is_scope_submitted) || runs.find(r => !r.is_scope_verified);
            if (pendingRun) {
                handleViewSnapshot(pendingRun);
            }
        }
    }, [triggerAutoOpen, loading, runs]);

    useEffect(() => {
        if (!loading && runs.length > 0) {
            const queryParams = new URLSearchParams(routerLocation.search);
            const urlRunId = queryParams.get("runId");
            if (urlRunId) {
                const runIdNum = parseInt(urlRunId, 10);
                const timer = setTimeout(() => {
                    const actionsElement = document.getElementById(`run-actions-${runIdNum}`);
                    if (actionsElement) {
                        actionsElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "end" });
                    } else {
                        const rowElement = document.getElementById(`run-row-${runIdNum}`);
                        if (rowElement) {
                            rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
                        }
                    }
                }, 150);
                return () => clearTimeout(timer);
            }
        }
    }, [loading, runs, routerLocation.search]);

    const generateDefaultTitle = (type: "quick" | "comprehensive") => {
        const programTitle = (program as any)?.program_title || (program as any)?.title || "Program";
        const typeLabel = type === "comprehensive" ? "comprehensive" : "quick";
        const baseTitle = `${programTitle} - ${typeLabel} run`;

        // Intelligent increment: Scan existing titles for the highest numeric suffix
        let maxSuffix = 0;
        const suffixRegex = new RegExp(`^${baseTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} - (\\d+)$`, 'i');

        runs.filter(r => r.run_type === type).forEach(run => {
            const match = run.run_title.match(suffixRegex);
            if (match) {
                const suffix = parseInt(match[1], 10);
                if (suffix > maxSuffix) {
                    maxSuffix = suffix;
                }
            }
        });

        // Use the higher of (maxSuffix + 1) or (count + 1) to be safe
        const typeSpecificRuns = runs.filter(r => r.run_type === type);
        const nextIndex = Math.max(maxSuffix + 1, typeSpecificRuns.length + 1);

        return `${baseTitle} - ${nextIndex}`;
    };

    const handleOpenAddModal = () => {
        setEditingRun(null);
        setFormData({
            run_title: generateDefaultTitle("quick"),
            start_date: new Date().toISOString().split('T')[0],
            run_type: "quick",
            status: "scheduled",
            release_notes: "",
            business_priorities: ""
        });
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (run: ProgramRun) => {
        if (!isAdmin) return; // Only admin can edit existing runs fully
        setEditingRun(run);
        setFormData({
            run_title: run.run_title,
            start_date: new Date(run.start_date).toISOString().split('T')[0],
            run_type: run.run_type,
            status: run.status as any,
            release_notes: run.release_notes || "",
            business_priorities: run.business_priorities || ""
        });
        setIsModalOpen(true);
    };

    const handleSaveRun = async () => {
        if (savingRun) return;
        setSavingRun(true);
        try {
            // Check quota before saving if new run
            if (!editingRun && quota && !isAdmin) {
                const type = formData.run_type;
                const remaining = quota[type].remaining;
                if (remaining <= 0) {
                    toast.error(`You have no remaining ${type} runs.`);
                    return;
                }
            }

            if (editingRun) {
                const res = await axios.patch(`/v2/program-runs/${editingRun.run_id}`, formData);
                toast.success("Run updated successfully");
                if (res.data) {
                    setRuns(prev => prev.map(r => r.run_id === editingRun.run_id ? { ...r, ...res.data } : r));
                }
            } else {
                const res = await axios.post("/v2/program-runs/create", {
                    program_id: parseInt(id!, 10),
                    ...formData,
                    // end_date is auto-calculated by backend based on run_type duration
                });
                toast.success("Run added successfully");
                if (res.data?.data) {
                    const createdRun = res.data.data;
                    setRuns(prev => [createdRun, ...prev.filter(r => r.run_id !== createdRun.run_id)]);
                }
            }
            setIsModalOpen(false);
            Promise.all([fetchRuns(), fetchQuota()]);
            if (refetchProgram) refetchProgram();
        } catch (error: any) {
            const msg = error.response?.data?.message || "Failed to save run";
            toast.error(msg);
        } finally {
            setSavingRun(false);
        }
    };

    const handleDeleteRun = async () => {
        if (!editingRun || !isAdmin) return;
        try {
            await axios.delete(`/v2/program-runs/${editingRun.run_id}`);
            toast.success("Run deleted successfully");
            setRuns(prev => prev.filter(r => r.run_id !== editingRun.run_id));
            setIsDeleteModalOpen(false);
            Promise.all([fetchRuns(), fetchQuota()]);
            if (refetchProgram) refetchProgram();
        } catch (error) {
            toast.error("Failed to delete run");
        }
    };

    const handleViewSnapshot = async (run: ProgramRun) => {
        setEditingRun(run);
        
        // Optimistic instant open: if snapshot is already present in memory on the run object, display immediately
        let hasInstantOpened = false;
        if (run.settings_snapshot) {
            try {
                const parsed = typeof run.settings_snapshot === "string" 
                    ? JSON.parse(run.settings_snapshot) 
                    : run.settings_snapshot;
                setSelectedRunSnapshot({
                    snapshot: parsed,
                    is_scope_verified: run.is_scope_verified,
                    is_scope_submitted: run.is_scope_submitted,
                    status: run.status
                });
                setTempSnapshot(parsed);
                setIsSnapshotModalOpen(true);
                setIsEditingSnapshot(false);
                hasInstantOpened = true;
            } catch (e) {
                console.error("Failed to parse local snapshot:", e);
            }
        }

        try {
            if (!hasInstantOpened) {
                setIsSnapshotModalOpen(true);
                setIsEditingSnapshot(false);
            }
            const response = await axios.get(`/v2/programs/runs/${run.run_id}/scope`);
            setSelectedRunSnapshot(response.data.data);
            setTempSnapshot(response.data.data.snapshot);
        } catch (error) {
            if (!hasInstantOpened) {
                toast.error("Failed to fetch run scope snapshot");
            }
        }
    };

    const handleUpdateSnapshot = async () => {
        if (!editingRun || !tempSnapshot) return;
        setSavingSnapshot(true);
        try {
            const patchRes = await axios.patch(`/v2/programs/runs/${editingRun.run_id}/scope`, { snapshot: tempSnapshot });
            toast.success("Run scope updated and synced to program");
            
            // Use patch response directly to avoid a redundant round-trip GET
            const data = patchRes.data?.data;
            if (data) {
                const formatted = data.snapshot ? data : {
                    snapshot: data,
                    is_scope_verified: selectedRunSnapshot?.is_scope_verified,
                    is_scope_submitted: selectedRunSnapshot?.is_scope_submitted,
                    status: selectedRunSnapshot?.status
                };
                setSelectedRunSnapshot(formatted);
            }
            setIsEditingSnapshot(false);
            fetchRuns(); // Program scope might have changed
        } catch (error) {
            toast.error("Failed to update run scope");
        } finally {
            setSavingSnapshot(false);
        }
    };

    const removeTargetFromSnapshot = (targetId: number) => {
        if (!tempSnapshot) return;
        const updatedTargets = (tempSnapshot.targets || []).filter((t: any) => t.target_id !== targetId);
        setTempSnapshot({ ...tempSnapshot, targets: updatedTargets });
    };

    const removeEndpointFromSnapshot = (targetId: number, resourceId: number) => {
        if (!tempSnapshot) return;
        const updatedTargets = tempSnapshot.targets.map((t: any) => {
            if (t.target_id === targetId) {
                const updatedEndpoints = (t.endpoints || []).filter((ep: any) => ep.resource_id !== resourceId);
                return { ...t, endpoints: updatedEndpoints };
            }
            return t;
        });
        setTempSnapshot({ ...tempSnapshot, targets: updatedTargets });
    };

    const fetchAvailableTargets = async () => {
        if (!program || !tempSnapshot) return;
        setLoadingAvailable(true);
        try {
            // Fetch all targets belonging to the asset with cycle occupancy check
            const isContinuous = program.scheduling_type === 'continuous';
            const isComprehensive = editingRun?.run_type === 'comprehensive';
            const checkOccupancy = isContinuous && isComprehensive;
            
            const response = await axios.get(`/assets/${program.asset_id}/targets?check_cycle_occupancy=${checkOccupancy}&exclude_program_id=${program.id || ''}&check_linked=false&include_resources=true`);
            const allAssetTargets = response.data || [];
            
            // Pre-seed cache with all target details and resources to avoid individual network round-trips
            allAssetTargets.forEach((t: any) => {
                targetDetailsCache.current.set(t.target_id, t);
            });

            // Filter out targets already in the snapshot
            const existingIds = new Set((tempSnapshot.targets || []).map((t: any) => t.target_id));
            const available = allAssetTargets.filter((t: any) => !existingIds.has(t.target_id));
            
            setAvailableSnapshotTargets(available);
        } catch (error) {
            console.error("Failed to fetch available targets:", error);
        } finally {
            setLoadingAvailable(false);
        }
    };

    const addTargetToSnapshot = async (targetId: number) => {
        if (!tempSnapshot) return;

        const targetInAvailable = availableSnapshotTargets.find(t => t.target_id === targetId);
        
        // Business users cannot add targets already covered in the cycle
        if (targetInAvailable?.already_covered && !isAdmin) {
            toast.error("This target has already been tested in current comprehensive cycle. You can use quick scan to test the target if urgent.", {
                duration: 5000,
                icon: '⚠️'
            });
            return;
        }

        try {
            // Fetch target details including resources (with caching)
            let targetDetails = targetDetailsCache.current.get(targetId);
            if (!targetDetails) {
                const response = await axios.get(`/assets/targets/${targetId}`);
                targetDetails = response.data;
                targetDetailsCache.current.set(targetId, targetDetails);
            }
            
            const newTarget = {
                target_id: targetDetails.target_id,
                target_name: targetDetails.target_name,
                target_type: targetDetails.target_type,
                target_value: targetDetails.target_value,
                status: targetDetails.status,
                metadata: targetDetails.metadata,
                endpoints: (targetDetails.resources || []).map((r: any) => ({
                    resource_id: r.resource_id,
                    value: r.value,
                    resource_type: r.resource_type,
                    http_method: r.http_method,
                    status: r.status
                }))
            };
            
            setTempSnapshot({
                ...tempSnapshot,
                targets: [...(tempSnapshot.targets || []), newTarget]
            });
            // Update available list
            setAvailableSnapshotTargets(prev => prev.filter(t => t.target_id !== targetId));
            toast.success(`${targetDetails.target_name} added to run scope`);
        } catch (error) {
            toast.error("Failed to add target to snapshot");
        }
    };

    const addAllFilteredToSnapshot = async () => {
        if (!tempSnapshot || filteredAvailableTargets.length === 0) return;
        
        // Filter out targets already in snapshot (safety)
        const toAdd = isAdmin 
            ? filteredAvailableTargets 
            : filteredAvailableTargets.filter((t: any) => !t.already_covered);

        if (toAdd.length === 0) {
            toast.error("No available targets to add in the current filter (already covered items excluded for business users).");
            return;
        }

        setLoadingAvailable(true);
        try {
            // Fetch details only for targets not yet in cache
            const missingTargets = toAdd.filter(t => !targetDetailsCache.current.has(t.target_id));
            if (missingTargets.length > 0) {
                const responses = await Promise.all(
                    missingTargets.map(t => axios.get(`/assets/targets/${t.target_id}`))
                );
                responses.forEach(res => {
                    targetDetailsCache.current.set(res.data.target_id, res.data);
                });
            }
            
            const newTargets = toAdd.map(t => {
                const targetDetails = targetDetailsCache.current.get(t.target_id) || t;
                return {
                    target_id: targetDetails.target_id,
                    target_name: targetDetails.target_name,
                    target_type: targetDetails.target_type,
                    target_value: targetDetails.target_value,
                    status: targetDetails.status,
                    metadata: targetDetails.metadata,
                    endpoints: (targetDetails.resources || []).map((r: any) => ({
                        resource_id: r.resource_id,
                        value: r.value,
                        resource_type: r.resource_type,
                        http_method: r.http_method,
                        status: r.status
                    }))
                };
            });

            setTempSnapshot({
                ...tempSnapshot,
                targets: [...(tempSnapshot.targets || []), ...newTargets]
            });
            
            const addedIds = new Set(toAdd.map(t => t.target_id));
            setAvailableSnapshotTargets(prev => prev.filter(t => !addedIds.has(t.target_id)));
            toast.success(`Added ${newTargets.length} targets to run scope`);
        } catch (error) {
            toast.error("Failed to add all filtered targets");
        } finally {
            setLoadingAvailable(false);
        }
    };

    const removeAllFilteredFromSnapshot = () => {
        if (!tempSnapshot || filteredSnapshotTargets.length === 0) return;
        
        const idsToRemove = new Set(filteredSnapshotTargets.map((t: any) => t.target_id));
        const updatedTargets = (tempSnapshot.targets || []).filter((t: any) => !idsToRemove.has(t.target_id));
        
        setTempSnapshot({ ...tempSnapshot, targets: updatedTargets });
        toast.success(`Removed ${idsToRemove.size} targets from run scope`);
        
        // Refetch available to sync back removed items to inventory list
        setTimeout(() => fetchAvailableTargets(), 0);
    };

    const handleVerifyScope = async (isDirect: boolean = false) => {
        if (!editingRun) return;
        
        // If it's a draft and we haven't confirmed yet, show modal
        if (!selectedRunSnapshot?.is_scope_submitted && !isDirect) {
            setIsDirectVerifyConfirmOpen(true);
            return;
        }

        setVerifying(true);
        try {
            await axios.patch(`/v2/programs/runs/${editingRun.run_id}/verify`);
            toast.success("Scope verified successfully");
            setIsSnapshotModalOpen(false);
            setIsDirectVerifyConfirmOpen(false);
            fetchRuns();
            if (refetchProgram) refetchProgram();
        } catch (error) {
            toast.error("Failed to verify scope");
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmitScope = async () => {
        if (!editingRun) return;
        setSubmitting(true);
        try {
            await axios.patch(`/v2/programs/runs/${editingRun.run_id}/submit`);
            toast.success("Scope submitted for verification");
            // Locally update submitted flag directly to avoid extra round-trip GET
            if (selectedRunSnapshot) {
                setSelectedRunSnapshot({
                    ...selectedRunSnapshot,
                    is_scope_submitted: true
                });
            }
            fetchRuns();
            if (refetchProgram) refetchProgram();
        } catch (error) {
            toast.error("Failed to submit scope");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePauseRun = async (run: ProgramRun) => {
        try {
            await axios.post(`/v2/program-runs/${run.run_id}/pause`);
            toast.success("Run paused successfully");
            fetchRuns();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to pause run");
        }
    };

    const handleResumeRun = async (run: ProgramRun) => {
        try {
            await axios.post(`/v2/program-runs/${run.run_id}/resume`);
            toast.success("Run resumed successfully");
            fetchRuns();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to resume run");
        }
    };

    const handleOpenRescheduleModal = (run: ProgramRun) => {
        setEditingRun(run);
        setRescheduleDate(new Date(run.start_date).toISOString().split('T')[0]);
        setRescheduleEndDate(new Date(run.end_date).toISOString().split('T')[0]);
        setIsRescheduleModalOpen(true);
    };

    const handleRescheduleSubmit = async () => {
        if (!editingRun || !rescheduleDate) return;
        try {
            await axios.patch(`/v2/program-runs/${editingRun.run_id}/reschedule`, { 
                start_date: rescheduleDate,
                end_date: isSuperAdmin ? rescheduleEndDate : undefined
            });
            toast.success("Run rescheduled successfully");
            setIsRescheduleModalOpen(false);
            fetchRuns();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to reschedule run");
        }
    };

    const handleRequestPentestReport = async (runId?: number) => {
        if (isRequestingReport) return;
        setIsRequestingReport(true);
        try {
            await createAutomatedReport({
                program_ids: [parseInt(id!, 10)],
                run_id: runId
            });
            toast.success("Pentest report requested successfully");
            setIsConfirmModalOpen(false);
            await fetchReports();
            // Dispatch custom event so parent ProgramPage can refresh its report states
            window.dispatchEvent(new CustomEvent("report-requested"));
        } catch (error: any) {
            const msg = error.response?.data?.message || "Failed to request pentest report";
            toast.error(msg);
        } finally {
            setIsRequestingReport(false);
        }
    };

    const handleRefreshSnapshot = async () => {
        if (!editingRun) return;
        setRefreshing(true);
        try {
            const refreshRes = await axios.post(`/v2/programs/runs/${editingRun.run_id}/refresh-snapshot`);
            toast.success("Snapshot refreshed successfully");
            // If backend returns data, set it directly without second GET round-trip
            const data = refreshRes.data?.data;
            if (data) {
                const formatted = data.snapshot ? data : {
                    snapshot: data,
                    is_scope_verified: selectedRunSnapshot?.is_scope_verified,
                    is_scope_submitted: selectedRunSnapshot?.is_scope_submitted,
                    status: selectedRunSnapshot?.status
                };
                setSelectedRunSnapshot(formatted);
                setTempSnapshot(formatted.snapshot);
            } else {
                const response = await axios.get(`/v2/programs/runs/${editingRun.run_id}/scope`);
                setSelectedRunSnapshot(response.data.data);
                setTempSnapshot(response.data.data.snapshot);
            }
            fetchRuns();
            if (refetchProgram) refetchProgram();
        } catch (error) {
            toast.error("Failed to refresh snapshot");
        } finally {
            setRefreshing(false);
        }
    };

    const getStatusBadge = (status: string, isVerified?: boolean, isSubmitted?: boolean) => {
        const statusConfig = {
            scheduled: { color: "bg-blue-100 text-blue-800", icon: HiClock, label: "Scheduled" },
            active: { color: "bg-green-100 text-green-800", icon: HiCheckCircle, label: "Active" },
            completed: { color: "bg-gray-100 text-gray-800", icon: HiCheckCircle, label: "Completed" },
            archived: { color: "bg-orange-100 text-orange-800", icon: HiExclamationCircle, label: "Archived" },
            paused: { color: "bg-amber-100 text-amber-800", icon: HiPause, label: "Paused" }
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.scheduled;
        const Icon = config.icon;

        return (
            <div className="flex flex-col gap-1 items-start">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${config.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {config.label}
                </span>
                {isVerified !== undefined && (
                    <div className="flex flex-col gap-0.5">
                        {isSubmitted ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${isVerified ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                                {isVerified ? "Scope Verified" : "Pending Verification"}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-amber-100 text-amber-700">
                                Draft Scope
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow border p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-500 mt-2">Loading scheduled runs...</p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Quota Stats (Only for authorized users and NON-one-off programs) */}
                {quota && (program as any)?.scheduling_type !== 'one_off' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Quick Runs */}
                        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 rounded-lg text-green-600">
                                        <HiLightningBolt className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900">Quick Runs</h4>
                                        <p className="text-xs text-gray-500 font-medium">
                                            {quota.quick.quota > 0 ? `${quota.quick.duration} days per run` : "Not Configured"}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {quota.quick.quota > 0 ? (
                                        <>
                                            <div className={`text-2xl font-black ${quota.quick.remaining > 0 ? 'text-gray-900' : 'text-red-600'}`}>
                                                {quota.quick.remaining}
                                            </div>
                                            <div className="text-xs font-semibold text-gray-400">Left of {quota.quick.quota}</div>
                                        </>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            Limit Not Set
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                                <div
                                    className={`h-1 rounded-full ${quota.quick.remaining === 0 ? 'bg-red-500' : 'bg-green-500'}`}
                                    style={{ width: quota.quick.quota > 0 ? `${Math.min(100, (quota.quick.remaining / quota.quick.quota) * 100)}%` : '100%' }}
                                ></div>
                            </div>
                        </div>

                        {/* Comprehensive Runs */}
                        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 rounded-lg text-green-600">
                                        <HiSearch className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900">Comprehensive Runs</h4>
                                        <p className="text-xs text-gray-500 font-medium">
                                            {quota.comprehensive.quota > 0 ? `${quota.comprehensive.duration} days per run` : "Not Configured"}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {quota.comprehensive.quota > 0 ? (
                                        <>
                                            <div className={`text-2xl font-black ${quota.comprehensive.remaining > 0 ? 'text-gray-900' : 'text-red-600'}`}>
                                                {quota.comprehensive.remaining}
                                            </div>
                                            <div className="text-xs font-semibold text-gray-400">Left of {quota.comprehensive.quota}</div>
                                        </>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            Limit Not Set
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                                <div
                                    className={`h-1 rounded-full ${quota.comprehensive.remaining === 0 ? 'bg-red-500' : 'bg-green-500'}`}
                                    style={{ width: quota.comprehensive.quota > 0 ? `${Math.min(100, (quota.comprehensive.remaining / quota.comprehensive.quota) * 100)}%` : '100%' }}
                                ></div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Dark navy header */}
                    <div className="bg-gray-900 px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-white">Scheduled Runs</h3>
                                <p className="text-xs text-gray-400 mt-0.5">View and manage testing runs for this program</p>
                            </div>
                            <div className="flex items-center gap-3">
                                {canManageRuns && ((program as any)?.scheduling_type !== 'one_off' || isSuperAdmin) && (
                                    // Only show Schedule Run if user is Admin OR if quotas are allocated
                                    (isAdmin || (quota && (quota.quick.quota > 0 || quota.comprehensive.quota > 0)))
                                ) && (
                                    <button
                                        onClick={handleOpenAddModal}
                                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-bold hover:bg-green-600 transition shadow-sm"
                                    >
                                        <HiPlus className="w-4 h-4" />
                                        Schedule Run
                                    </button>
                                )}
                                <span className="text-sm font-semibold text-gray-900 bg-white px-3 py-1.5 rounded-lg">
                                    {runs.length} {runs.length === 1 ? 'Run' : 'Runs'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {runs.length === 0 ? (
                        <div className="p-12 text-center">
                            {quota && quota.quick.quota === 0 && quota.comprehensive.quota === 0 ? (
                                <div className="max-w-sm mx-auto">
                                    <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-100">
                                        <HiClock className="w-8 h-8 text-amber-500 animate-pulse" />
                                    </div>
                                    <h3 className="text-base font-bold text-gray-900 mb-2">Wait for CTB Admins Review</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed">
                                        Please wait for CTB admins review to allocate your runs. 
                                        Once the admin configures your run details in the company management section, you'll be able to schedule and manage runs.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <HiCalendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                    <h3 className="text-sm font-medium text-gray-900 mb-1">No Scheduled Runs</h3>
                                    <p className="text-xs text-gray-500">This program doesn't have any scheduled runs yet.</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Run Title</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Start Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">End Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                        {hasAnyFeedback && (
                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Feedback</th>
                                        )}
                                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {runs.map((run, index) => {
                                        const queryParams = new URLSearchParams(routerLocation.search);
                                        const urlRunId = queryParams.get("runId");
                                        const isHighlighted = run.run_id === Number(urlRunId);
                                        return (
                                            <tr 
                                                key={run.run_id} 
                                                id={`run-row-${run.run_id}`}
                                                className={clsx(
                                                    "hover:bg-gray-50 transition-all duration-500",
                                                    isHighlighted && "bg-blue-50/70 border-l-4 border-blue-500 ring-2 ring-blue-500/30"
                                                )}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">

                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xs">
                                                            {index + 1}
                                                        </div>
                                                        <div className="text-sm font-semibold text-gray-900">{run.run_title}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${run.run_type === 'comprehensive'
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : 'bg-indigo-100 text-indigo-700'
                                                        }`}>
                                                        {run.run_type === 'comprehensive' ? 'Comprehensive' : 'Quick'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{formatDate(run.start_date)}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{formatDate(run.end_date)}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(run.status, run.is_scope_verified, run.is_scope_submitted)}
                                                </td>
                                                {hasAnyFeedback && (
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {(run.feedback?.status === 'submitted' || run.feedback?.status === 'completed' || run.feedback?.status === 'resolved') ? (
                                                            run.feedback.overall_program_rating ? (
                                                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 border border-yellow-200 rounded-lg text-xs font-semibold text-yellow-800 shadow-sm" title={`${run.feedback.status.charAt(0).toUpperCase() + run.feedback.status.slice(1)} Feedback`}>
                                                                    <FiStar className="w-3.5 h-3.5 fill-yellow-400 text-yellow-500" />
                                                                    <span>{run.feedback.overall_program_rating.toFixed(1)}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 capitalize">{run.feedback.status}</span>
                                                            )
                                                        ) : run.feedback?.status === 'skipped' || run.feedback?.is_skipped ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">Skipped</span>
                                                        ) : run.feedback?.status === 'requested' || pendingFeedbacksForThisProgram.some(pf => pf.run_id === run.run_id) ? (
                                                            <div className="inline-flex items-center gap-1.5">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Requested</span>
                                                                {isSuperAdmin && (
                                                                    <button
                                                                        onClick={() => executeRunUndoFeedback(run.run_id)}
                                                                        disabled={isUndoingFeedback}
                                                                        className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                                                        title="Take Back Feedback Request"
                                                                    >
                                                                        <FiRotateCcw className="w-3.5 h-3.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 italic">No feedback</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td 
                                                    id={`run-actions-${run.run_id}`}
                                                    className="px-6 py-4 whitespace-nowrap text-right text-sm"
                                                >
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => handleViewSnapshot(run)}
                                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all shadow-sm ${!run.is_scope_submitted
                                                                    ? "bg-amber-500 text-white hover:bg-amber-600 ring-2 ring-amber-500/20"
                                                                    : !run.is_scope_verified
                                                                        ? "bg-blue-600 text-white hover:bg-blue-700 ring-2 ring-blue-500/20"
                                                                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                                                }`}
                                                            title={!run.is_scope_submitted ? (isAdmin ? "Review and Verify Scope" : "Review and Submit Scope") : !run.is_scope_verified ? "Verify Scope Snapshot" : "View Scope Snapshot"}
                                                        >
                                                            {!run.is_scope_submitted ? (
                                                                <><HiPencil className="w-3.5 h-3.5" /> {isAdmin ? "Review & Verify" : "Review Scope"}</>
                                                            ) : !run.is_scope_verified ? (
                                                                <><HiEye className="w-3.5 h-3.5" /> Verify Scope</>
                                                            ) : (
                                                                <><HiEye className="w-3.5 h-3.5" /> View Scope</>
                                                            )}
                                                        </button>

                                                        {/* Manage Checklist Action */}
                                                        {isAdmin && (
                                                            <button
                                                                onClick={() => navigate(`${routerLocation.pathname}?runId=${run.run_id}&section=Test Checklists`, { replace: true })}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all shadow-sm ring-2 ring-indigo-500/20"
                                                                title="Manage Run Checklists"
                                                            >
                                                                <HiShieldCheck className="w-3.5 h-3.5" /> Manage Checklist
                                                            </button>
                                                        )}
                                                        {/* Reschedule/Pause/Resume Actions - Visible to Admins & Business Users */}
                                                        {canManageRuns && (
                                                            <>
                                                                {/* Reschedule Check: Status scheduled + start > now + 2 days (Bypassed for SuperAdmins) */}
                                                                {((run.status === 'scheduled') || isSuperAdmin) && (
                                                                    (() => {
                                                                        const start = new Date(run.start_date).getTime();
                                                                        const now = Date.now();
                                                                        const twoDays = 2 * 24 * 60 * 60 * 1000;
                                                                        if (isSuperAdmin || start - now > twoDays) {
                                                                            return (
                                                                                <button
                                                                                    onClick={() => handleOpenRescheduleModal(run)}
                                                                                    className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-md transition"
                                                                                    title="Reschedule Run"
                                                                                >
                                                                                    <HiCalendar className="w-4 h-4" />
                                                                                </button>
                                                                            );
                                                                        }
                                                                        return null;
                                                                    })()
                                                                )}

                                                                {/* Pause/Resume Actions */}
                                                                {run.status === 'active' && (
                                                                    <button
                                                                        onClick={() => handlePauseRun(run)}
                                                                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-md transition"
                                                                        title="Pause Run"
                                                                    >
                                                                        <HiPause className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                                {run.status === 'paused' && (
                                                                    <button
                                                                        onClick={() => handleResumeRun(run)}
                                                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition"
                                                                        title="Resume Run"
                                                                    >
                                                                        <HiPlay className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Request Report - Visible if end_date has passed */}
                                                        {(() => {
                                                            const endDate = new Date(run.end_date).getTime();
                                                            const now = Date.now();
                                                            if (isAuthorizedForReport && endDate <= now) {
                                                                const reportInfo = getRunReportState(run.run_id);

                                                                if (reportInfo.status === 'requested' && (!reportInfo.approvedReports || reportInfo.approvedReports.length === 0)) {
                                                                    return (
                                                                        <button
                                                                            disabled
                                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-md text-xs font-bold cursor-not-allowed shadow-none"
                                                                            title="The pentest report is currently under review. Please wait."
                                                                        >
                                                                            <FaShieldAlt className="w-3.5 h-3.5 text-gray-350" />
                                                                            Report — In QA
                                                                        </button>
                                                                    );
                                                                }

                                                                if (reportInfo.status === 'generated' || reportInfo.status === 'requested' || (reportInfo.approvedReports && reportInfo.approvedReports.length > 0)) {
                                                                    const hasPending = reportInfo.status === 'requested';
                                                                    return (
                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                            {/* Show In QA badge when new version is pending */}
                                                                            {hasPending && (
                                                                                <button
                                                                                    disabled
                                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-md text-xs font-bold cursor-not-allowed shadow-none"
                                                                                >
                                                                                    <FaShieldAlt className="w-3.5 h-3.5 text-gray-350" />
                                                                                    {reportInfo.approvedReports && reportInfo.approvedReports.length > 0 ? 'New Version \u2014 In QA' : 'Report \u2014 In QA'}
                                                                                </button>
                                                                            )}
                                                                            {/* Always show status label and download for approved versions */}
                                                                            {!hasPending && (
                                                                                <button
                                                                                    disabled
                                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-md text-xs font-bold cursor-not-allowed shadow-none"
                                                                                >
                                                                                    <FaShieldAlt className="w-3.5 h-3.5 text-gray-350" />
                                                                                    Report Generated — Available to Download
                                                                                </button>
                                                                            )}
                                                                            {reportInfo.approvedReports && reportInfo.approvedReports.length > 1 ? (
                                                                                <select
                                                                                    onChange={(e) => {
                                                                                        if (e.target.value) {
                                                                                            navigate(`/dashboard/pentest-reports/${e.target.value}/edit`);
                                                                                        }
                                                                                    }}
                                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 rounded-md transition-all text-xs font-bold shadow-sm outline-none cursor-pointer"
                                                                                    defaultValue=""
                                                                                >
                                                                                    <option value="" disabled>Download Version...</option>
                                                                                    {reportInfo.approvedReports.map((item: any, idx: number) => {
                                                                                        const reportObj = item.report || item;
                                                                                        const versionNum = item.version_number || reportObj.version_number || (idx + 1);
                                                                                        return (
                                                                                            <option key={reportObj.id} value={reportObj.id}>
                                                                                                {`Version ${versionNum}`}
                                                                                            </option>
                                                                                        );
                                                                                    })}
                                                                                </select>
                                                                            ) : (() => {
                                                                                // Always use the approved report's id for download, not the pending one
                                                                                const approvedItem = reportInfo.approvedReports?.[0];
                                                                                const approvedObj = approvedItem?.report || approvedItem;
                                                                                const downloadId = approvedObj?.id || reportInfo.reportId;
                                                                                return downloadId ? (
                                                                                    <button
                                                                                        onClick={() => navigate(`/dashboard/pentest-reports/${downloadId}/edit`)}
                                                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 rounded-md transition-all text-xs font-bold shadow-sm"
                                                                                        title="Download PDF Report"
                                                                                    >
                                                                                        <FiDownload className="w-3.5 h-3.5" />
                                                                                        Download
                                                                                    </button>
                                                                                ) : null;
                                                                            })()}
                                                                            {isSuperAdmin && !hasPending && (
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setSelectedRunForReport(run.run_id);
                                                                                        setIsConfirmModalOpen(true);
                                                                                    }}
                                                                                    disabled={isRequestingReport || !['business_review', 'business_approved'].includes(reportInfo.latestReportObj?.status)}
                                                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-md transition-all text-xs font-bold shadow-sm ${
                                                                                        isRequestingReport || !['business_review', 'business_approved'].includes(reportInfo.latestReportObj?.status)
                                                                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed shadow-none'
                                                                                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200'
                                                                                    }`}
                                                                                    title={
                                                                                        reportInfo.latestReportObj && !['business_review', 'business_approved'].includes(reportInfo.latestReportObj.status)
                                                                                            ? `Cannot request new version: previous version is in ${reportInfo.latestReportObj.status} status`
                                                                                            : 'Request a new version of the report'
                                                                                    }
                                                                                >
                                                                                    <FaShieldAlt className="w-3.5 h-3.5" />
                                                                                    Request New Version
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }

                                                                return (
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedRunForReport(run.run_id);
                                                                            setIsConfirmModalOpen(true);
                                                                        }}
                                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-md transition-all text-xs font-bold shadow-sm"
                                                                        title="Request Pentest Report"
                                                                    >
                                                                        <FaShieldAlt className="w-3.5 h-3.5" />
                                                                        Request Report
                                                                    </button>
                                                                );
                                                            }
                                                            return null;
                                                        })()}

                                                        {/* Feedback Action - Visible to Business Users if feedback is requested */}
                                                        {isFeedbackAllowedBusinessUser && (run.feedback?.status === 'requested' || pendingFeedbacksForThisProgram.some(pf => pf.run_id === run.run_id)) && (
                                                            <button
                                                                onClick={() => navigate(`/dashboard/programs/${id}/feedback?runId=${run.run_id}`)}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-all text-xs font-bold shadow-sm"
                                                                title="Give Feedback"
                                                            >
                                                                <FiMessageSquare className="w-3.5 h-3.5" />
                                                                Give Feedback
                                                            </button>
                                                        )}

                                                        {isAdmin && (
                                                            <>
                                                                {isSuperAdmin && (run.status === 'completed' && (!run.feedback || run.feedback?.status === 'skipped')) && (
                                                                    <button
                                                                        onClick={() => executeRunRequestFeedback(run.run_id)}
                                                                        disabled={isRequestingFeedback}
                                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-md text-xs font-bold transition-all shadow-sm"
                                                                        title="Request Feedback for this Run"
                                                                    >
                                                                        <FiMessageSquare className="w-3.5 h-3.5" />
                                                                        Request Feedback
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={() => handleOpenEditModal(run)}
                                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition"
                                                                    title="Edit Run"
                                                                >
                                                                    <HiPencil className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => { setEditingRun(run); setIsDeleteModalOpen(true); }}
                                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition"
                                                                    title="Delete Run"
                                                                >
                                                                    <HiTrash className="w-4 h-4" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Edit/Add Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-[100] overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen p-4">
                            <div className="fixed inset-0 bg-black/40" onClick={() => setIsModalOpen(false)} />
                            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl z-10 transition-all">
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                                    <h3 className="text-xl font-bold text-gray-900">
                                        {editingRun ? 'Edit Scheduled Run' : 'Schedule New Run'}
                                    </h3>
                                    <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition p-1 rounded-full hover:bg-gray-100">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>

                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Left Column */}
                                        <div className="space-y-4">
                                            {/* Run Type Cards */}
                                            {!editingRun && (
                                                (!quota || (quota.quick.quota === 0 && quota.comprehensive.quota === 0)) ? (
                                                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center text-xs">
                                                        <HiExclamationCircle className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                                                        <h4 className="font-bold text-gray-900">Quotas Exceeded</h4>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <button
                                                            onClick={() => {
                                                                if (quota && quota.quick.quota > 0) {
                                                                    setFormData({ ...formData, run_type: "quick", run_title: generateDefaultTitle("quick") });
                                                                }
                                                            }}
                                                            disabled={!quota || quota.quick.quota === 0}
                                                            className={`relative p-3 rounded-xl border-2 text-center transition-all
                                                            ${formData.run_type === 'quick' ? 'border-blue-500 bg-blue-50/20' : 'border-gray-100 hover:border-gray-200'}
                                                            ${(!quota || quota.quick.quota === 0) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                                        `}
                                                        >
                                                            <HiLightningBolt className={`w-5 h-5 mx-auto mb-1 ${formData.run_type === 'quick' ? 'text-blue-500' : 'text-gray-300'}`} />
                                                            <h4 className="font-bold text-gray-900 text-[11px] uppercase tracking-wider">Quick</h4>
                                                            {quota && quota.quick.quota > 0 && (
                                                                <div className="text-[10px] font-bold text-blue-600 mt-0.5">{quota.quick.remaining} Left</div>
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (quota && quota.comprehensive.quota > 0) {
                                                                    setFormData({ ...formData, run_type: "comprehensive", run_title: generateDefaultTitle("comprehensive") });
                                                                }
                                                            }}
                                                            disabled={!quota || quota.comprehensive.quota === 0}
                                                            className={`relative p-3 rounded-xl border-2 text-center transition-all
                                                            ${formData.run_type === 'comprehensive' ? 'border-green-500 bg-green-50/20' : 'border-gray-100 hover:border-gray-200'}
                                                            ${(!quota || quota.comprehensive.quota === 0) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                                        `}
                                                        >
                                                            <HiSearch className={`w-5 h-5 mx-auto mb-1 ${formData.run_type === 'comprehensive' ? 'text-green-500' : 'text-gray-300'}`} />
                                                            <h4 className="font-bold text-gray-900 text-[11px] uppercase tracking-wider">Comprehensive</h4>
                                                            {quota && quota.comprehensive.quota > 0 && (
                                                                <div className="text-[10px] font-bold text-green-600 mt-0.5">{quota.comprehensive.remaining} Left</div>
                                                            )}
                                                        </button>
                                                    </div>
                                                )
                                            )}

                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Run Title</label>
                                                    <input
                                                        type="text"
                                                        value={formData.run_title}
                                                        onChange={(e) => setFormData({ ...formData, run_title: e.target.value })}
                                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Start Date</label>
                                                        <input
                                                            type="date"
                                                            value={formData.start_date}
                                                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition"
                                                        />
                                                    </div>
                                                    {isAdmin && (
                                                        <div>
                                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Status</label>
                                                            <select
                                                                value={formData.status}
                                                                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition"
                                                            >
                                                                <option value="scheduled">Scheduled</option>
                                                                <option value="active">Active</option>
                                                                <option value="completed">Completed</option>
                                                                <option value="archived">Archived</option>
                                                                <option value="paused">Paused</option>
                                                            </select>
                                                        </div>
                                                    )}
                                                </div>

                                                {!editingRun && quota && (quota.quick.quota > 0 || quota.comprehensive.quota > 0) && (
                                                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-[10px] text-blue-700 flex items-center gap-2">
                                                        <HiClock className="w-4 h-4" />
                                                        <span>Duration: <strong>{formData.run_type === 'quick' ? quota.quick.duration : quota.comprehensive.duration} days</strong></span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right Column */}
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Release Notes</label>
                                                <textarea
                                                    value={formData.release_notes}
                                                    onChange={(e) => setFormData({ ...formData, release_notes: e.target.value })}
                                                    placeholder="Major changes or new features..."
                                                    rows={4}
                                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition resize-none"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Business Priorities</label>
                                                <textarea
                                                    value={formData.business_priorities}
                                                    onChange={(e) => setFormData({ ...formData, business_priorities: e.target.value })}
                                                    placeholder="Where should researchers focus?"
                                                    rows={4}
                                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition resize-none"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-50">
                                        <button onClick={() => setIsModalOpen(false)} className="px-5 py-2 text-sm font-medium text-gray-500 hover:text-gray-900">
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveRun}
                                            disabled={savingRun || (!editingRun && (!quota || (formData.run_type === 'quick' && quota.quick.quota === 0) || (formData.run_type === 'comprehensive' && quota.comprehensive.quota === 0)))}
                                            className="px-8 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-black transition disabled:opacity-50"
                                        >
                                            {savingRun ? 'Saving...' : editingRun ? 'Update Schedule' : 'Schedule Run'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {isDeleteModalOpen && (
                    <div className="fixed inset-0 z-[100] overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <HiTrash className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <h3 className="text-lg leading-6 font-bold text-gray-900">Delete Scheduled Run</h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to delete <span className="font-bold text-gray-900">{editingRun?.run_title}</span>? This action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                                    <button
                                        onClick={handleDeleteRun}
                                        className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-bold text-white hover:bg-red-700 focus:outline-none sm:w-auto sm:text-sm"
                                    >
                                        Delete Run
                                    </button>
                                    <button
                                        onClick={() => setIsDeleteModalOpen(false)}
                                        className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Reschedule Modal */}
                {isRescheduleModalOpen && (
                    <div className="fixed inset-0 z-[100] overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <HiCalendar className="h-6 w-6 text-orange-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-bold text-gray-900">Reschedule Run</h3>
                                            <div className="mt-2 text-sm text-gray-500">
                                                <p className="mb-4">Select a new start date for <span className="font-bold">{editingRun?.run_title}</span>. The end date will be adjusted automatically.</p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">New Start Date</label>
                                                        <input
                                                            type="date"
                                                            value={rescheduleDate}
                                                            onChange={(e) => setRescheduleDate(e.target.value)}
                                                            className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                                        />
                                                    </div>
                                                    {isSuperAdmin && (
                                                        <div>
                                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">New End Date</label>
                                                            <input
                                                                type="date"
                                                                value={rescheduleEndDate}
                                                                onChange={(e) => setRescheduleEndDate(e.target.value)}
                                                                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {!isSuperAdmin && (
                                                    <p className="mt-2 text-xs text-orange-600">
                                                        * Note: You can only reschedule to a date at least 2 days from today.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                                    <button
                                        onClick={handleRescheduleSubmit}
                                        className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-bold text-white hover:bg-orange-700 focus:outline-none sm:w-auto sm:text-sm"
                                    >
                                        Reschedule
                                    </button>
                                    <button
                                        onClick={() => setIsRescheduleModalOpen(false)}
                                        className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Scope Snapshot Modal - Redesigned for Premium UX */}
                {
                    isSnapshotModalOpen && selectedRunSnapshot && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity" onClick={() => setIsSnapshotModalOpen(false)}></div>
                            
                            <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-[90vw] overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[92vh]">
                                {/* Header / Context Bar */}
                                <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-800 rounded-lg">
                                            <HiShieldCheck className="w-5 h-5 text-blue-400" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest leading-none">Scope Snapshot</h3>
                                            </div>
                                            <h2 className="text-white font-bold truncate max-w-md">
                                                {program?.title} • <span className="text-blue-400">{editingRun?.run_title}</span>
                                            </h2>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {(!selectedRunSnapshot.is_scope_verified || isSuperAdmin) && (
                                            <button
                                                onClick={() => {
                                                    setIsEditingSnapshot(!isEditingSnapshot);
                                                    if (!isEditingSnapshot) fetchAvailableTargets();
                                                }}
                                                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all duration-300 border ${
                                                    isEditingSnapshot 
                                                    ? 'bg-red-500 text-white border-red-400 shadow-lg shadow-red-500/30' 
                                                    : 'bg-white/10 text-white border-white/10 hover:bg-white/20'
                                                }`}
                                            >
                                                {isEditingSnapshot ? <HiTrash className="w-4 h-4" /> : <HiPencil className="w-4 h-4" />}
                                                {isEditingSnapshot ? 'Cancel Editing' : 'Edit Scope'}
                                            </button>
                                        )}
                                        {selectedRunSnapshot.is_scope_verified ? (
                                            <div className="flex items-center gap-1.5 px-3 py-1 bg-green-500/10 text-green-400 border border-green-500/20 rounded-full text-[10px] font-black uppercase tracking-wider">
                                                <HiShieldCheck className="w-3.5 h-3.5" /> Verified
                                            </div>
                                        ) : selectedRunSnapshot.is_scope_submitted ? (
                                            <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full text-[10px] font-black uppercase tracking-wider">
                                                <HiClock className="w-3.5 h-3.5" /> Pending
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full text-[10px] font-black uppercase tracking-wider">
                                                <HiExclamationCircle className="w-3.5 h-3.5" /> Draft
                                            </div>
                                        )}
                                        <button 
                                            onClick={() => setIsSnapshotModalOpen(false)}
                                            className="text-slate-400 hover:text-white transition p-1 hover:bg-slate-800 rounded ml-2"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Body Content - Refactored for 2-Column Layout */}
                                <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50/50">
                                    <div className="flex flex-col lg:flex-row min-h-full">
                                        {/* Left Sidebar: Asset Context */}
                                        <div className="w-full lg:w-80 bg-white border-r border-slate-200 p-6 space-y-8 lg:sticky lg:top-0 h-fit">
                                            {/* Header Captured At */}
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                                    <HiClock className="w-3.5 h-3.5" />
                                                    Captured At
                                                </div>
                                                <div className="text-sm font-bold text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                                    {selectedRunSnapshot.snapshot?.capturedAt ? new Date(selectedRunSnapshot.snapshot.capturedAt).toLocaleString() : 'N/A'}
                                                </div>
                                            </div>

                                            {/* Asset Information Sidebar Card */}
                                            <div className="space-y-6">
                                                <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                                    Primary Inventory Source
                                                </h4>
                                                
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Program Name</label>
                                                        <div className="text-sm font-bold text-slate-900 leading-tight">
                                                            {program?.title || 'Active Program'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Asset Name</label>
                                                        <div className="text-sm font-bold text-slate-900 leading-tight">
                                                            {tempSnapshot?.asset?.name || selectedRunSnapshot.snapshot?.asset?.name || 'Loading...'}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-6">
                                                        <div className="flex-1">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter block mb-2">Type</label>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {(() => {
                                                                    const asset = tempSnapshot?.asset || selectedRunSnapshot.snapshot?.asset;
                                                                    const rawType = asset?.type || 'N/A';
                                                                    const otherType = asset?.type_other || asset?.primary_type_other || asset?.category_other;
                                                                    
                                                                    let types: string[] = [];
                                                                    if (Array.isArray(rawType)) {
                                                                        types = rawType;
                                                                    } else {
                                                                        types = rawType.toString().split(',').map((t: string) => t.trim()).filter(Boolean);
                                                                    }

                                                                    return types.map((t, idx) => {
                                                                        const displayValue = (t.toLowerCase() === 'other' && otherType) ? otherType : t;
                                                                        return (
                                                                            <span key={idx} className="text-[10px] font-black text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-md capitalize">
                                                                                {displayValue}
                                                                            </span>
                                                                        );
                                                                    });
                                                                })()}
                                                            </div>
                                                        </div>
                                                        <div className="w-24">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter block mb-2">Criticality</label>
                                                            <div className={`text-[10px] font-black px-2 py-0.5 rounded-md uppercase border ${(tempSnapshot?.asset?.criticality || selectedRunSnapshot.snapshot?.asset?.criticality) === 'HIGH' ? 'bg-orange-50 text-orange-600 border-orange-100' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                                                {(tempSnapshot?.asset?.criticality || selectedRunSnapshot.snapshot?.asset?.criticality || 'MEDIUM')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Run Context */}
                                            {(editingRun?.release_notes || editingRun?.business_priorities) && (
                                                <div className="space-y-6 pt-4 border-t border-slate-100">
                                                    <h4 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1.5">
                                                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                                                        Run Context
                                                    </h4>
                                                    
                                                    {editingRun?.release_notes && (
                                                        <div>
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Release Notes</label>
                                                            <div className="text-sm text-slate-600 italic leading-snug mt-1">
                                                                {editingRun.release_notes}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {editingRun?.business_priorities && (
                                                        <div>
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Business Priorities</label>
                                                            <div className="text-sm text-slate-600 italic leading-snug mt-1">
                                                                {editingRun.business_priorities}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Inventory Hint */}
                                            <div className="p-4 bg-slate-900 rounded-2xl text-white/90 space-y-2 shadow-xl shadow-slate-900/10">
                                                <div className="flex items-center gap-2 text-blue-400">
                                                    <HiExclamationCircle className="w-4 h-4" />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">Inventory Guide</span>
                                                </div>
                                                <p className="text-[11px] leading-relaxed text-slate-300 font-medium">
                                                    Only targets synchronized in your <span className="text-white font-bold underline cursor-pointer" onClick={() => navigate('/dashboard/assets')}>Global Asset Inventory</span> will appear here. To include new targets in this snapshot, add them there first.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Right Column: Targets & Resources */}
                                        <div className="flex-1 p-8 space-y-8">
                                            {selectedRunSnapshot.snapshot && (
                                                <div className="bg-white/60 backdrop-blur-sm p-4 rounded-3xl border border-slate-200 shadow-sm space-y-4">
                                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                        <div className="relative flex-1 max-w-lg">
                                                            <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                                            <input 
                                                                type="text"
                                                                value={snapshotSearchTerm}
                                                                onChange={(e) => setSnapshotSearchTerm(e.target.value)}
                                                                placeholder="Search snapshot items..."
                                                                className="w-full h-10 pl-11 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400"
                                                            />
                                                        </div>
                                                        
                                                        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar scrollbar-hide">
                                                            {[
                                                                { id: 'all', label: 'All', icon: <FiLayers /> },
                                                                { id: 'web', label: 'Web', icon: <FiGlobe /> },
                                                                { id: 'api', label: 'API', icon: <FiTarget /> },
                                                                { id: 'mobile', label: 'Mobile', icon: <FiSmartphone /> },
                                                                { id: 'infra', label: 'Infra', icon: <FiServer /> },
                                                                { id: 'cloud', label: 'Cloud', icon: <FiCloud /> }
                                                            ].map(f => (
                                                                <button
                                                                    key={f.id}
                                                                    onClick={() => setSnapshotActiveFilter(f.id as any)}
                                                                    className={clsx(
                                                                        "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest whitespace-nowrap transition-all border",
                                                                        snapshotActiveFilter === f.id 
                                                                            ? "bg-slate-900 text-blue-400 border-slate-900 shadow-md" 
                                                                            : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                                                                    )}
                                                                >
                                                                    {f.icon} {f.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {!selectedRunSnapshot.snapshot ? (
                                                <div className="py-24 text-center bg-white rounded-3xl border-2 border-dashed border-slate-200 shadow-inner max-w-lg mx-auto">
                                                    <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6 rotate-12 group-hover:rotate-0 transition-all duration-500 border border-slate-100 shadow-sm">
                                                        <HiExclamationCircle className="w-10 h-10 text-slate-300" />
                                                    </div>
                                                    <h4 className="text-slate-900 font-black text-xl tracking-tight">Scope Not Captured</h4>
                                                    <p className="text-slate-500 text-sm max-w-xs mx-auto mt-2 leading-relaxed">This scheduled run hasn't frozen an asset snapshot yet. Pulse the inventory to initialize scope.</p>
                                                    {(isSuperAdmin || (isAdmin && editingRun?.status === 'scheduled')) && (
                                                        <button
                                                            onClick={handleRefreshSnapshot}
                                                            disabled={refreshing}
                                                            className="mt-8 inline-flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-2xl text-sm font-black hover:bg-blue-700 shadow-xl shadow-blue-500/30 active:scale-95 transition-all"
                                                        >
                                                            {refreshing ? <HiRefresh className="w-5 h-5 animate-spin" /> : <HiLightningBolt className="w-5 h-5" />}
                                                            {refreshing ? 'Initializing...' : 'Initialize Scope Now'}
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Edit Mode: Add Targets Expanded */}
                                                    {isEditingSnapshot && (
                                                        <div className="bg-blue-600 rounded-3xl p-6 text-white shadow-2xl shadow-blue-600/20 border border-blue-500/50 animate-in slide-in-from-top-4 duration-500">
                                                            <div className="flex items-center justify-between mb-8">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-md">
                                                                        <HiPlus className="w-6 h-6" />
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="text-lg font-black tracking-tight">Sync Inventory Items</h4>
                                                                        <p className="text-blue-100/60 text-xs font-bold uppercase tracking-widest mt-0.5">Global Inventory Link Active</p>
                                                                    </div>
                                                                </div>                                                                 <div className="flex items-center gap-3">
                                                                    <button 
                                                                        onClick={addAllFilteredToSnapshot}
                                                                        className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border border-white/20 active:scale-95"
                                                                    >
                                                                        <HiPlus className="w-3.5 h-3.5" /> Select All Filtered
                                                                    </button>
                                                                    <div className="hidden md:block text-[10px] font-black uppercase tracking-widest bg-white/10 px-3 py-1.5 rounded-full border border-white/10 opacity-60">
                                                                        {filteredAvailableTargets.length} available
                                                                    </div>
                                                                </div>

                                                            </div>

                                                            {loadingAvailable ? (
                                                                <div className="flex items-center justify-center py-12">
                                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/50"></div>
                                                                </div>
                                                            ) : (availableSnapshotTargets.length === 0 || filteredAvailableTargets.length === 0) ? (
                                                                <div className="text-center py-12 bg-black/10 rounded-2xl border border-white/10 backdrop-blur-sm">
                                                                    <p className="text-sm text-blue-100 font-bold italic">
                                                                        {availableSnapshotTargets.length === 0 ? "All inventory components are already synchronized." : "No matching available targets."}
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                                                    {filteredAvailableTargets.map((t: any) => (
                                                                        <button
                                                                            key={t.target_id}
                                                                            onClick={() => addTargetToSnapshot(t.target_id)}
                                                                            className={clsx(
                                                                                "flex items-center justify-between px-5 py-4 border rounded-2xl text-left transition-all group relative overflow-hidden backdrop-blur-sm",
                                                                                t.already_covered 
                                                                                    ? "bg-white/5 border-white/5 opacity-60 grayscale cursor-pointer" 
                                                                                    : "bg-white/10 hover:bg-white/20 border-white/10 active:scale-[0.98]"
                                                                            )}
                                                                        >
                                                                            <div className="truncate pr-4 relative z-10">
                                                                                <div className="flex items-center gap-2 mb-1.5">
                                                                                    <span className="text-[9px] font-black uppercase text-blue-200 tracking-widest">{t.target_type}</span>
                                                                                    {t.already_covered && (
                                                                                        <span className="text-[8px] font-black bg-blue-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-tighter">In Cycle</span>
                                                                                    )}
                                                                                </div>
                                                                                <span className="text-sm font-bold truncate block">{t.target_name}</span>
                                                                            </div>
                                                                            <div className="relative z-10 p-1.5 bg-white/20 rounded-xl group-hover:bg-white/40 transition-colors shadow-sm">
                                                                                {(t.already_covered && !isAdmin) ? <HiExclamationCircle className="w-4 h-4 text-amber-300" /> : t.already_covered ? <HiCheckCircle className="w-4 h-4" /> : <HiPlus className="w-4 h-4" />}
                                                                            </div>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}


                                                    {/* Main Section Header */}
                                                    <div className="flex items-center justify-between px-2">
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 bg-slate-900 rounded-xl shadow-lg">
                                                                <HiShieldCheck className="w-5 h-5 text-blue-400" />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none mb-1">Scope Synchronization</h4>
                                                                <h2 className="text-lg font-black text-slate-900">Targets & Resources</h2>
                                                            </div>
                                                        </div>                                                         <div className="flex items-center gap-3">
                                                            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-2xl border border-slate-200 shadow-sm">
                                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{snapshotSearchTerm || snapshotActiveFilter !== 'all' ? 'Filtered' : 'Active Scope'}</span>
                                                                <span className={clsx("w-1.5 h-1.5 rounded-full animate-pulse", (snapshotSearchTerm || snapshotActiveFilter !== 'all') ? "bg-blue-500" : "bg-green-500")}></span>
                                                                <span className="text-sm font-black text-slate-900">{filteredSnapshotTargets.length}</span>
                                                            </div>
                                                            {isEditingSnapshot && filteredSnapshotTargets.length > 0 && (
                                                                <button 
                                                                    onClick={removeAllFilteredFromSnapshot}
                                                                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border border-red-100 active:scale-95 shadow-sm"
                                                                >
                                                                    <HiTrash className="w-4 h-4" /> Remove All Filtered
                                                                </button>
                                                            )}
                                                        </div>

                                                    </div>

                                                    {/* Targets Grid Container */}
                                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                                        {filteredSnapshotTargets.length === 0 ? (
                                                            <div className="col-span-full py-20 text-center bg-white rounded-3xl border-2 border-dashed border-slate-200 animate-in zoom-in-95 duration-300">
                                                                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                                                                    <HiSearch className="w-8 h-8 text-slate-300" />
                                                                </div>
                                                                <h5 className="text-slate-900 font-black mb-1">No items found</h5>
                                                                <p className="text-slate-400 text-xs font-medium">Try adjusting your filters or search term.</p>
                                                            </div>
                                                        ) : filteredSnapshotTargets.map((target: any) => (
                                                            <div key={target.target_id} className="bg-white border border-slate-200 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden flex flex-col h-full border-b-4 border-b-slate-50 hover:border-b-blue-500">
                                                                {/* Target Top Section */}
                                                                <div className="p-6 space-y-4 flex-1">
                                                                    <div className="flex items-start justify-between gap-4">
                                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                            {isEditingSnapshot && (
                                                                                <button
                                                                                    onClick={() => removeTargetFromSnapshot(target.target_id)}
                                                                                    className="p-2.5 text-red-500 bg-red-50 hover:bg-red-100 rounded-2xl transition-all shadow-sm border border-red-100 flex-shrink-0"
                                                                                    title="Remove Target"
                                                                                >
                                                                                    <HiTrash className="w-4 h-4" />
                                                                                </button>
                                                                            )}
                                                                            <div className="min-w-0 flex-1 overflow-hidden">
                                                                                <div className="flex items-center gap-2 mb-1 min-w-0">
                                                                                    <span className="text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-100 uppercase tracking-tighter flex-shrink-0">
                                                                                        {target.target_type}
                                                                                    </span>
                                                                                    <span className="text-sm font-black text-slate-900 truncate" title={target.target_name}>{target.target_name}</span>
                                                                                </div>
                                                                                {(!(target.target_value === "Uploaded Binary" || target.target_value === target.metadata?.binary_name) || !target.metadata?.binary_url) && (
                                                                                    <div className="text-[11px] font-mono text-slate-400 truncate bg-slate-50 px-2 py-1 rounded-lg border border-slate-100 inline-block max-w-full">
                                                                                        {target.target_value}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {(target.metadata?.binary_name && target.metadata?.binary_url) && (
                                                                            <a
                                                                                href={target.metadata.binary_url}
                                                                                download={target.metadata.binary_name}
                                                                                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 hover:bg-slate-100 rounded-2xl border border-slate-200 transition-all shadow-sm group/binary text-[10px] font-bold text-blue-600"
                                                                                title={`Download ${target.metadata.binary_name}`}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <HiLightningBolt className="w-4 h-4 text-blue-500 group-hover/binary:scale-110 transition-transform" />
                                                                                {target.metadata.binary_name}
                                                                            </a>
                                                                        )}
                                                                    </div>

                                                                    {/* Endpoint List Header */}
                                                                    <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Resources</span>
                                                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-black rounded-lg border border-slate-200">
                                                                                {(target.endpoints || []).length}
                                                                            </span>
                                                                        </div>
                                                                        <div className="h-0.5 flex-1 bg-slate-50 mx-4"></div>
                                                                        <div className="flex -space-x-1">
                                                                            {/* Dots for visual appeal */}
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-200"></div>
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-300"></div>
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Scrollable Resources within Card */}
                                                                    <div className="bg-slate-50/50 rounded-2xl overflow-hidden max-h-[180px] overflow-y-auto custom-scrollbar border border-slate-100 shadow-inner">
                                                                        {(target.endpoints || []).length > 0 ? (
                                                                            <div className="divide-y divide-slate-100">
                                                                                {(target.endpoints || []).map((ep: any) => (
                                                                                    <div key={ep.resource_id} className="flex items-center px-4 py-2.5 hover:bg-white transition-colors group/ep">
                                                                                        <div className="flex items-center gap-2 min-w-[70px]">
                                                                                            {isEditingSnapshot && (
                                                                                                <button
                                                                                                    onClick={() => removeEndpointFromSnapshot(target.target_id, ep.resource_id)}
                                                                                                    className="p-1 text-red-400 hover:bg-red-50 rounded-lg opacity-0 group-hover/ep:opacity-100 transition shadow-sm bg-white"
                                                                                                >
                                                                                                    <HiTrash className="w-3 h-3" />
                                                                                                </button>
                                                                                            )}
                                                                                            <span className="text-[8px] font-black text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-200 min-w-[36px] text-center">
                                                                                                {ep.http_method || 'ANY'}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0 ml-2">
                                                                                            <div className="text-[10px] font-mono text-slate-600 truncate">{ep.value}</div>
                                                                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mt-0.5">{ep.resource_type}</span>
                                                                                        </div>
                                                                                        <div className="w-2 h-2 rounded-full bg-green-500 border-2 border-green-100 shadow-sm shadow-green-500/20"></div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="py-8 text-center bg-white/50">
                                                                                <p className="text-[10px] text-slate-400 font-bold italic">No child resources defined.</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Footer Actions */}
                                <div className="bg-slate-50 border-t border-slate-200 p-8 flex items-center justify-between gap-6">
                                    <div className="flex items-center gap-4">
                                        <button 
                                            onClick={() => setIsSnapshotModalOpen(false)}
                                            className="px-8 py-3 bg-white text-slate-600 rounded-2xl text-xs font-black hover:bg-slate-100 border border-slate-200 shadow-sm active:scale-95 transition-all"
                                        >
                                            Dismiss
                                        </button>
                                        <div className="hidden lg:flex items-center gap-2.5 px-4 py-2 bg-blue-50/50 text-blue-600/60 rounded-xl border border-blue-100/50 text-[10px] font-black uppercase tracking-widest">
                                            <HiClock className="w-4 h-4 flex-shrink-0 animate-pulse" />
                                            Snapshot captures the scope at this exact timestamp
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {isEditingSnapshot ? (
                                            <button
                                                onClick={handleUpdateSnapshot}
                                                disabled={savingSnapshot}
                                                className="flex items-center gap-2 px-10 py-3 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-black shadow-xl shadow-slate-900/20 active:scale-95 transition-all disabled:opacity-50"
                                            >
                                                {savingSnapshot ? <HiRefresh className="w-4 h-4 animate-spin" /> : <HiCheckCircle className="w-4 h-4" />}
                                                {savingSnapshot ? 'Saving Changes...' : 'Save Snapshot'}
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                {isAdmin && !selectedRunSnapshot.is_scope_verified && (selectedRunSnapshot.is_scope_submitted || isSuperAdmin) && selectedRunSnapshot.snapshot && (
                                                    <button
                                                        onClick={() => handleVerifyScope()}
                                                        disabled={verifying}
                                                        className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-green-700 shadow-xl shadow-green-500/20 active:scale-95 transition-all disabled:opacity-50"
                                                    >
                                                        {verifying ? <HiRefresh className="w-4 h-4 animate-spin" /> : <HiShieldCheck className="w-4 h-4" />}
                                                        {verifying ? 'Verifying...' : 'Verify Scope Now'}
                                                    </button>
                                                )}

                                                {isBusinessUser && !selectedRunSnapshot.is_scope_submitted && selectedRunSnapshot.snapshot && (
                                                    <button
                                                        onClick={handleSubmitScope}
                                                        disabled={submitting}
                                                        className="flex items-center gap-2 px-10 py-3 bg-blue-600 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-500/30 active:scale-95 transition-all disabled:opacity-50"
                                                    >
                                                        {submitting ? <HiRefresh className="w-4 h-4 animate-spin" /> : <HiClock className="w-4 h-4" />}
                                                        {submitting ? 'Submitting...' : 'Submit to Admin'}
                                                    </button>
                                                )}

                                                {(isSuperAdmin || (editingRun?.status === 'scheduled' && !selectedRunSnapshot.is_scope_verified)) && (
                                                    <button
                                                        onClick={handleRefreshSnapshot}
                                                        disabled={refreshing}
                                                        className="flex items-center gap-2 px-6 py-3 border border-slate-200 bg-white text-slate-700 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-300 active:scale-95 transition-all disabled:opacity-50"
                                                    >
                                                        <HiRefresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                                        {refreshing ? 'Refreshing...' : 'Pull Inventory'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
            </div>

            <PentestReportConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleRequestPentestReport}
                programId={parseInt(id!, 10)}
                fixedRunId={selectedRunForReport}
            />

            {/* Direct Verification Confirmation Modal */}
            {isDirectVerifyConfirmOpen && (
                <div className="fixed inset-0 z-[110] overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setIsDirectVerifyConfirmOpen(false)}>
                            <div className="absolute inset-0 bg-slate-900 opacity-75 backdrop-blur-sm"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-slate-100">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-8 sm:pb-6">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-2xl bg-amber-50 sm:mx-0 sm:h-12 sm:w-12">
                                        <HiExclamationCircle className="h-8 w-8 text-amber-500" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-6 sm:text-left">
                                        <h3 className="text-xl leading-6 font-black text-slate-900 tracking-tight">Direct Verification</h3>
                                        <div className="mt-3">
                                            <p className="text-sm text-slate-500 leading-relaxed">
                                                Standard workflow requires the <span className="font-bold text-slate-700">Business user</span> to review and submit this scope for verification. 
                                                By verifying now, you are <span className="text-amber-600 font-bold">skipping this intermediate step</span>.
                                            </p>
                                            <p className="text-sm text-slate-500 mt-4 font-medium italic bg-slate-50 p-3 rounded-xl border border-slate-100">
                                                "Business verifies for admin and subadmin and submits to you for verification. If you are doing this, it's like skipping the business workflow."
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-slate-50 px-4 py-4 sm:px-8 sm:flex sm:flex-row-reverse gap-3">
                                <button
                                    onClick={() => handleVerifyScope(true)}
                                    className="w-full inline-flex justify-center items-center gap-2 rounded-2xl border border-transparent shadow-lg px-6 py-3 bg-slate-900 text-sm font-black text-white hover:bg-black transition-all active:scale-95 sm:w-auto"
                                >
                                    <HiShieldCheck className="w-4 h-4" /> Confirm & Verify
                                </button>
                                <button
                                    onClick={() => setIsDirectVerifyConfirmOpen(false)}
                                    className="mt-3 w-full inline-flex justify-center rounded-2xl border border-slate-200 shadow-sm px-6 py-3 bg-white text-sm font-black text-slate-600 hover:bg-slate-50 transition-all active:scale-95 sm:mt-0 sm:w-auto"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ScheduledRuns;
